import { useEffect, useState, useCallback } from "react";
import { LevelBadge } from "@/components/StaffLevels";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { Sparkles, Globe, X, ChevronLeft, ChevronRight } from "lucide-react";
import { API } from "@/App";
import ChatWidget from "@/components/ChatWidget";
import MiniSiteBooking from "@/components/MiniSiteBooking";
import NotFoundPage from "@/pages/NotFoundPage";
import { useTranslation } from "@/i18n/translations";

/**
 * Public mini-site rendered at chatity.io/<slug>.
 *
 * Universal wellness template (beauty salons, massage, spa, hair, brows etc.).
 * Layout:
 *   - SiteHeader
 *   - Hero with salon name + logo + key CTA (open chat)
 *   - "About" section parsed from salon_info (preserves line breaks)
 *   - Chat: sidebar (right, 360px) on desktop, bottom drawer on mobile
 *   - Footer
 *
 * Source: source=mini-site goes via ?ref=<slug>&open=1 into ChatWidget.
 */
export default function MiniSitePage() {
  const { slug } = useParams();
  const { t, lang } = useTranslation();
  const [status, setStatus] = useState("loading"); // loading | ok | notfound
  const [data, setData] = useState(null);
  const [lightboxIdx, setLightboxIdx] = useState(-1); // -1 = closed; otherwise index into photos[]
  const [phoneShown, setPhoneShown] = useState(false); // DIKIDI-style "Show phone" reveal
  const [showAllServices, setShowAllServices] = useState(false);
  const [showAllTeam, setShowAllTeam] = useState(false);
  const [openCats, setOpenCats] = useState({});
  const [bookCfg, setBookCfg] = useState(null); // public booking config (auto services/staff/branches)

  // ── Auto-translation state ──
  // translation: { source_lang, target_lang, translated } | null
  // showOriginal: user clicked "Show original" → render source content, skip refetch
  // translateError: true when Gemini call failed → render red banner + original content
  const [translation, setTranslation] = useState(null);
  const [showOriginal, setShowOriginal] = useState(() => {
    try { return localStorage.getItem("minisite_show_original") === "1"; } catch { return false; }
  });
  const [translateError, setTranslateError] = useState(false);
  const [translateLoading, setTranslateLoading] = useState(false);
  // Visitor browser language (base ISO code, e.g. 'fr', 'zh', 'de')
  const visitorLang = (typeof navigator !== "undefined"
    ? (navigator.language || navigator.userLanguage || "en")
    : "en").split("-")[0].toLowerCase();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/slug/resolve/${encodeURIComponent(slug)}`);
        if (!cancelled) {
          setData(r.data);
          setStatus("ok");
        }
      } catch {
        if (!cancelled) setStatus("notfound");
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  // ── Public booking config: services/staff/branches auto from Team section ──
  useEffect(() => {
    if (!data?.salon_id) return;
    let cancelled = false;
    axios.get(`${API}/public/booking/${data.salon_id}/config`)
      .then((r) => { if (!cancelled) setBookCfg(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [data?.salon_id]);

  // ── Fetch translation once data is loaded (unless user opted out) ──
  useEffect(() => {
    if (!data || showOriginal) return;
    let cancelled = false;
    setTranslateLoading(true);
    setTranslateError(false);
    (async () => {
      try {
        const r = await axios.post(`${API}/minisite/translate`, {
          slug: data.slug,
          target_lang: visitorLang,
        });
        if (!cancelled) setTranslation(r.data || null);
      } catch {
        if (!cancelled) {
          setTranslation(null);
          setTranslateError(true);
        }
      } finally {
        if (!cancelled) setTranslateLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [data, showOriginal, visitorLang]);

  // Inject Open Graph / Twitter Card meta tags directly into <head>. Using
  // useEffect instead of <Helmet> because <Helmet>'s meta-tag updates were
  // unreliable in this app (HelmetProvider double-mount + dedupe race).
  // Direct DOM writes match what SEOHead.js does for the homepage and
  // consistently survive social-crawler scrapes.
  useEffect(() => {
    if (!data) return;
    const ms = data.mini_site || {};
    const seoTitleEff = `${((ms.seo_title || "").trim() || data.salon_name)} — Chatity`;
    const aboutSrc = (ms.about && ms.about.trim()) ? ms.about : (data.salon_info || "");
    const seoDescEff = ((ms.seo_description || "").trim() || aboutSrc.slice(0, 160)).replace(/\s+/g, " ").trim();
    const seoKeywords = (ms.seo_keywords || "").trim();
    const origin = (typeof window !== "undefined" && window.location?.origin) || "https://chatity.io";
    const canonical = `https://chatity.io/${data.slug}`;
    const ogImg = data.cover_url
      ? (/^https?:\/\//i.test(data.cover_url) ? data.cover_url : `${origin}${data.cover_url}`)
      : null;
    const locale = ({ ru: "ru_RU", en: "en_US", fr: "fr_FR", tr: "tr_TR" }[lang]) || "en_US";

    const setMeta = (selector, attrName, attrVal, content) => {
      let m = document.head.querySelector(selector);
      if (!m) {
        m = document.createElement("meta");
        m.setAttribute(attrName, attrVal);
        document.head.appendChild(m);
      }
      m.setAttribute("content", content);
    };

    const pairs = [
      ['meta[property="og:type"]', 'property', 'og:type', 'website'],
      ['meta[property="og:site_name"]', 'property', 'og:site_name', 'Chatity'],
      ['meta[property="og:title"]', 'property', 'og:title', seoTitleEff],
      ['meta[property="og:description"]', 'property', 'og:description', seoDescEff],
      ['meta[property="og:url"]', 'property', 'og:url', canonical],
      ['meta[property="og:locale"]', 'property', 'og:locale', locale],
      ['meta[name="twitter:card"]', 'name', 'twitter:card', ogImg ? 'summary_large_image' : 'summary'],
      ['meta[name="twitter:title"]', 'name', 'twitter:title', seoTitleEff],
      ['meta[name="twitter:description"]', 'name', 'twitter:description', seoDescEff],
      ['meta[name="twitter:url"]', 'name', 'twitter:url', canonical],
      ['meta[name="description"]', 'name', 'description', seoDescEff],
    ];
    if (seoKeywords) {
      pairs.push(['meta[name="keywords"]', 'name', 'keywords', seoKeywords]);
    }
    if (ogImg) {
      pairs.push(
        ['meta[property="og:image"]', 'property', 'og:image', ogImg],
        ['meta[property="og:image:secure_url"]', 'property', 'og:image:secure_url', ogImg],
        ['meta[property="og:image:alt"]', 'property', 'og:image:alt', data.salon_name],
        ['meta[property="og:image:width"]', 'property', 'og:image:width', '1200'],
        ['meta[property="og:image:height"]', 'property', 'og:image:height', '630'],
        ['meta[name="twitter:image"]', 'name', 'twitter:image', ogImg],
      );
    }
    for (const [sel, an, av, c] of pairs) setMeta(sel, an, av, c);
    document.title = seoTitleEff;
    document.documentElement.lang = lang || 'en';
  }, [data, lang]);

  if (status === "loading") {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center text-gray-400">
        ...
      </div>
    );
  }
  if (status === "notfound") {
    return <NotFoundPage context={{ searchedSlug: slug }} />;
  }

  const seoTitle = `${data.salon_name} — Chatity`;
  const msRaw = data.mini_site || {};

  // ── Merge translation into mini_site fields (when available + not opted out) ──
  // Source content always stays in msRaw; translated content overlays on top
  // unless the source language already equals the visitor language or the user
  // clicked "Show original".
  const trObj = translation && translation.translated ? translation.translated : null;
  const trSourceLang = (translation && translation.source_lang) || "";
  const trIsApplied = !!(trObj && !showOriginal && trSourceLang && trSourceLang !== visitorLang);

  // Build a service-id → translated-service lookup so we can overlay names
  // and descriptions while preserving prices / durations / ordering.
  const trServicesById = {};
  if (trIsApplied && Array.isArray(trObj.services)) {
    for (const ts of trObj.services) {
      if (!ts || !ts.id) continue;
      const itemsById = {};
      for (const ti of (ts.items || [])) {
        if (ti && ti.id) itemsById[ti.id] = ti;
      }
      trServicesById[ts.id] = { ...ts, _itemsById: itemsById };
    }
  }

  // Booking catalog (db.salon_services) + staff levels translations, keyed by id
  const trCatalog = {};
  const trLevels = {};
  if (trIsApplied) {
    for (const c of (Array.isArray(trObj.catalog) ? trObj.catalog : [])) if (c && c.id) trCatalog[c.id] = c;
    for (const l of (Array.isArray(trObj.levels) ? trObj.levels : [])) if (l && l.id) trLevels[l.id] = l.name;
  }
  const trBranches = {};
  if (trIsApplied) for (const b of (Array.isArray(trObj.branches) ? trObj.branches : [])) if (b && b.id && b.name) trBranches[b.id] = b.name;
  const bookCfgTr = bookCfg && trIsApplied
    ? { ...bookCfg, branches: (bookCfg.branches || []).map((b) => ({ ...b, name: trBranches[b.id] || b.name })) }
    : bookCfg;
  const trCategoryByRaw = {};
  if (trIsApplied) {
    for (const s of (bookCfg?.services || [])) {
      const tc = trCatalog[s.id];
      if (tc && tc.category && s.category && !trCategoryByRaw[s.category]) trCategoryByRaw[s.category] = tc.category;
    }
  }
  const trServiceNameByRaw = {};
  if (trIsApplied) {
    for (const s of (bookCfg?.services || [])) {
      const tc = trCatalog[s.id];
      if (tc && tc.name) trServiceNameByRaw[s.name] = tc.name;
    }
  }

  const ms = trIsApplied
    ? {
        ...msRaw,
        tagline: (typeof trObj.tagline === "string" && trObj.tagline) || msRaw.tagline,
        about: (typeof trObj.about === "string" && trObj.about) || msRaw.about,
        hours: (typeof trObj.hours === "string" && trObj.hours) || msRaw.hours,
        services: (msRaw.services || []).map((cat) => {
          const catId = cat?.id || cat?.name || "";
          const trCat = trServicesById[catId];
          if (!trCat) return cat;
          return {
            ...cat,
            name: trCat.name || cat.name,
            description: trCat.description || cat.description,
            items: (cat.items || []).map((it) => {
              const itId = it?.id || it?.name || "";
              const trIt = trCat._itemsById[itId];
              if (!trIt) return it;
              return {
                ...it,
                name: trIt.name || it.name,
                description: trIt.description || it.description,
              };
            }),
          };
        }),
      }
    : msRaw;
  // ── UI label resolver: when translation is applied, use translated UI labels
  // (section headings, CTA texts). Falls back to t() when no translation is
  // applied or specific label was not provided by the model.
  const trUi = (trIsApplied && trObj && typeof trObj.ui === "object" && trObj.ui) || null;
  const tUi = (uiKey, i18nKey) => {
    if (trUi && typeof trUi[uiKey] === "string" && trUi[uiKey].trim()) {
      return trUi[uiKey];
    }
    return t(i18nKey);
  };

  const aboutSource = (ms.about && ms.about.trim()) ? ms.about : (data.salon_info || "");
  const seoDesc = aboutSource.slice(0, 160).replace(/\s+/g, " ").trim();

  // Build absolute URLs for OG tags (social crawlers need fully-qualified URLs).
  // `data.cover_url` may be a relative '/api/files/...' from object storage — prepend origin.
  const siteOrigin =
    (typeof window !== "undefined" && window.location?.origin) || "https://chatity.io";
  const canonicalUrl = `https://chatity.io/${data.slug}`;
  const ogImage = data.cover_url
    ? (/^https?:\/\//i.test(data.cover_url) ? data.cover_url : `${siteOrigin}${data.cover_url}`)
    : null;
  // og:locale map — fall back to en_US for unknown languages.
  const ogLocale = ({ ru: "ru_RU", en: "en_US", fr: "fr_FR", tr: "tr_TR" }[lang]) || "en_US";

  // Parse about text into paragraphs preserving blank-line breaks
  const infoBlocks = aboutSource
    .split(/\n{2,}/)
    .map((s) => s.trim())
    .filter(Boolean);

  const services = (ms.services || []).filter((c) => (c?.name || "").trim() || (c?.items || []).length);
  const photos = (ms.photos || []).filter((p) => (p?.url || "").trim());
  const social = ms.social || {};
  const hours = (ms.hours || "").trim();
  const currency = (ms.currency || "€").trim();
  const accent = /^#[0-9a-fA-F]{6}$/.test(ms.accent_color || "") ? ms.accent_color : "#111111";
  const manualTeam = (ms.team || []).filter((m) => (m?.name || "").trim());
  // Auto team from the Team section (db.staff) — overrides manual list when present
  const autoTeam = (bookCfg?.staff || []).map((s) => ({ id: s.id, name: s.name, role: "", photo_url: s.photo_url || "", color: s.color, level_name: trLevels[s.level] || s.level_name, stars: s.stars, branch_ids: s.branch_ids || [], services: (s.services || []).map((n) => trServiceNameByRaw[n] || n) }));
  const team = autoTeam.length ? autoTeam : manualTeam;
  const teamBranches = (bookCfgTr?.branches || []);
  // Masters grouped by branch (only when the salon has several branches)
  const teamGroups = autoTeam.length && teamBranches.length > 1
    ? teamBranches.map((b) => ({ branch: b, members: autoTeam.filter((m) => !m.branch_ids.length || m.branch_ids.includes(b.id)) })).filter((g) => g.members.length)
    : [{ branch: null, members: team }];
  const pickStaff = (m) => {
    if (m.id) window.dispatchEvent(new CustomEvent("chatity_ms_pick_staff", { detail: { staffId: m.id } }));
  };
  const openChat = () => window.dispatchEvent(new MessageEvent("message", { data: "chatity_force_open" }));
  const manualServices = services.flatMap((cat) =>
    (cat.items || []).filter((it) => (it.name || "").trim()).map((it) => ({ ...it, catName: cat.name || "" }))
  );
  // Auto services from the Team section (db.salon_services) — overrides manual list when present
  const autoServices = (bookCfg?.services || []).map((s) => ({
    id: s.id, name: trCatalog[s.id]?.name || s.name,
    description: trCatalog[s.id]?.description || "",
    price: s.price_from != null ? String(s.price_from) : (s.price != null ? String(s.price) : ""),
    priceFrom: Object.keys(s.level_prices || {}).length > 0 && new Set([s.price, ...Object.values(s.level_prices || {})].filter((v) => v != null)).size > 1,
    duration: s.duration_min ? `${s.duration_min} ${t("ms.book.minutes") !== "ms.book.minutes" ? t("ms.book.minutes") : "min"}` : "",
    catName: (s.category && (trCategoryByRaw[s.category] || s.category)) || "",
    branchLabel: (s.branch_ids || []).length && (bookCfg?.branches || []).length > 1
      ? s.branch_ids.map((id) => (bookCfgTr.branches.find((b) => b.id === id) || {}).name).filter(Boolean).join(" / ") : "",
  }));
  const flatServices = autoServices.length
    ? [...autoServices].sort((a, b) => (a.catName || "\uffff").localeCompare(b.catName || "\uffff"))
    : manualServices;
  const bookAction = bookCfg?.online_booking
    ? () => document.getElementById("ms-booking")?.scrollIntoView({ behavior: "smooth" })
    : openChat;
  const bookingWidgetShown = !!(bookCfg && (bookCfg.online_booking || bookCfg.calendar_enabled === false));
  const visibleServices = showAllServices ? flatServices : flatServices.slice(0, 8);
  const heroImage = data.cover_url || (photos[0] && photos[0].url) || "";
  const CURRENCY_RE = /[€$₽₺£₴₸¥₩₪₦₫฿]/g;
  const formatPrice = (raw) => {
    const v = String(raw ?? "").trim();
    if (!v) return "";
    // If user typed a letter currency code like "USD 50" or "50 USD" — show as-is
    if (/[A-Za-zА-Яа-я]/.test(v)) return v;
    // Strip any currency symbols and surrounding whitespace, then apply the chosen prefix.
    // This way legacy values like "€50" or "40€" or "$ 30" all normalize to "{currency}<number>".
    const cleaned = v.replace(CURRENCY_RE, "").trim();
    return cleaned ? `${currency}${cleaned}` : v;
  };
  // Branches from the CRM (address / phone / weekly schedule) — override legacy single address & hours text
  const branches = (bookCfgTr?.branches || []).filter((b) => (b.address || "").trim() || Object.values(b.schedule || {}).some((d) => d?.enabled));
  const defaultBranch = branches.find((b) => b.is_default) || branches[0];
  const DAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
  const dayLabels = (t("ms.days") !== "ms.days" ? t("ms.days") : (lang === "ru" ? "Пн,Вт,Ср,Чт,Пт,Сб,Вс" : lang === "fr" ? "Lun,Mar,Mer,Jeu,Ven,Sam,Dim" : lang === "tr" ? "Pzt,Sal,Çar,Per,Cum,Cmt,Paz" : "Mon,Tue,Wed,Thu,Fri,Sat,Sun")).split(",");
  const closedLabel = lang === "ru" ? "выходной" : lang === "fr" ? "fermé" : lang === "tr" ? "kapalı" : "closed";
  const scheduleLines = (sch) => {
    const rows = DAY_KEYS.map((k, i) => ({ d: dayLabels[i], v: sch?.[k]?.enabled ? `${sch[k].start}–${sch[k].end}` : closedLabel }));
    const out = [];
    rows.forEach((r) => {
      const last = out[out.length - 1];
      if (last && last.v === r.v) { last.to = r.d; } else out.push({ from: r.d, to: null, v: r.v });
    });
    return out.map((g) => ({ label: g.to ? `${g.from}–${g.to}` : g.from, value: g.v }));
  };
  const address = (social.address || "").trim() || (defaultBranch?.address || "").trim();
  const mapEmbedUrl = address
    ? `https://maps.google.com/maps?q=${encodeURIComponent(address)}&hl=${encodeURIComponent(lang || "en")}&z=15&output=embed`
    : null;
  const mapLinkUrl = address
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`
    : null;

  const socialIcons = [
    { key: "phone", label: "📞", text: social.phone, href: social.phone ? `tel:${social.phone.replace(/\s/g, "")}` : null },
    { key: "whatsapp", label: "💬", text: social.whatsapp ? tUi("contact_whatsapp", "miniSite.contact.whatsapp") : null,
      href: social.whatsapp ? `https://wa.me/${social.whatsapp.replace(/[^0-9]/g, "")}` : null },
    { key: "email", label: "📧", text: social.email, href: social.email ? `mailto:${social.email}` : null },
    { key: "instagram", label: "📷", text: "Instagram", href: social.instagram || null },
    { key: "facebook", label: "📘", text: "Facebook", href: social.facebook || null },
    { key: "tiktok", label: "📺", text: "TikTok", href: social.tiktok || null },
  ].filter((i) => i.href && i.text);

  return (
    <>
      <Helmet>
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>

      <style>{`
        @keyframes ms-fade-up {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes ms-shimmer {
          0%   { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        .ms-fade { animation: ms-fade-up 0.6s ease-out both; }
        .ms-accent-border { border-color: var(--ms-accent) !important; }
        .ms-gradient-bg {
          background: linear-gradient(135deg, #fef3c7 0%, #fce7f3 50%, #e0e7ff 100%);
          background-size: 200% 200%;
          animation: ms-shimmer 12s ease infinite;
        }
      `}</style>

      <div className="min-h-screen flex flex-col bg-gray-100 text-black" style={{ "--ms-accent": accent }}>
        {/* Auto-translation status banners */}
        {translateError && (
          <div
            className="bg-red-50 border-b-2 border-red-500 text-red-800 px-4 py-2.5 text-sm md:text-base flex items-center justify-center gap-2"
            data-testid="minisite-translate-error"
            role="alert"
          >
            <span>⚠️</span>
            <span>{t("miniSite.translate.failed") || "Auto-translation failed. Showing original."}</span>
          </div>
        )}
        {trIsApplied && (
          <div
            className="bg-gray-50 border-b border-gray-200 text-gray-700 px-4 py-2 text-xs md:text-sm flex items-center justify-center gap-3"
            data-testid="minisite-translate-banner"
          >
            <span className="inline-flex items-center gap-1.5">
              <Globe className="w-3.5 h-3.5" />
              <span>{t("miniSite.translate.byAi") || "Translated automatically by AI"}</span>
            </span>
            <button
              type="button"
              onClick={() => {
                try { localStorage.setItem("minisite_show_original", "1"); } catch (_e) { /* ignore */ }
                setShowOriginal(true);
              }}
              className="underline underline-offset-2 font-semibold hover:text-black transition-colors"
              data-testid="minisite-show-original"
            >
              {t("miniSite.translate.showOriginal") || "Show original"}
            </button>
          </div>
        )}
        {showOriginal && trSourceLang && trSourceLang !== visitorLang && (
          <div
            className="bg-gray-50 border-b border-gray-200 text-gray-700 px-4 py-2 text-xs md:text-sm flex items-center justify-center gap-3"
            data-testid="minisite-original-banner"
          >
            <span>{t("miniSite.translate.viewingOriginal") || "Viewing original"}</span>
            <button
              type="button"
              onClick={() => {
                try { localStorage.removeItem("minisite_show_original"); } catch (_e) { /* ignore */ }
                setShowOriginal(false);
              }}
              className="underline underline-offset-2 font-semibold hover:text-black transition-colors"
              data-testid="minisite-translate-back"
            >
              {t("miniSite.translate.translateBack") || "Translate to my language"}
            </button>
          </div>
        )}
        {translateLoading && !trIsApplied && !translateError && (
          <div
            className="bg-gray-50 border-b border-gray-200 text-gray-500 px-4 py-1.5 text-xs flex items-center justify-center"
            data-testid="minisite-translate-loading"
          >
            <span className="inline-flex items-center gap-1.5">
              <Globe className="w-3 h-3 animate-pulse" />
              <span>{t("miniSite.translate.loading") || "Auto-translating…"}</span>
            </span>
          </div>
        )}
        <div className="flex-1 pt-6 md:pt-8">
          <div className="container mx-auto max-w-4xl px-3 md:px-4 pb-12">
            {/* Hero — DIKIDI-style card: photo left, info right */}
            <section className="ms-fade bg-white rounded-2xl shadow-sm border border-gray-200 p-4 md:p-5 mb-4" data-testid="minisite-hero-card">
              <div className="grid md:grid-cols-2 gap-4 md:gap-6">
                {heroImage ? (
                  <button
                    type="button"
                    onClick={() => photos.length > 0 && setLightboxIdx(0)}
                    className={`relative rounded-xl overflow-hidden bg-gray-100 h-56 md:h-64 ${photos.length > 0 ? "cursor-zoom-in" : "cursor-default"}`}
                    data-testid="minisite-hero-photo"
                  >
                    <img src={heroImage} alt={data.salon_name} className="w-full h-full object-cover" />
                    {photos.length > 1 && (
                      <span className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded-full">
                        📷 {photos.length}
                      </span>
                    )}
                  </button>
                ) : (
                  <div className="rounded-xl h-56 md:h-64 ms-gradient-bg flex items-center justify-center">
                    <Sparkles className="w-10 h-10 text-gray-400" />
                  </div>
                )}

                <div className="flex flex-col">
                  <div className="flex items-start gap-3">
                    {data.logo_url ? (
                      <img
                        src={data.logo_url}
                        alt={data.salon_name}
                        className="w-14 h-14 md:w-16 md:h-16 rounded-full border border-gray-200 object-cover bg-white shadow-sm"
                        data-testid="minisite-logo"
                      />
                    ) : (
                      <div className="w-14 h-14 md:w-16 md:h-16 rounded-full border border-gray-200 bg-gray-50 flex items-center justify-center shadow-sm">
                        <Sparkles className="w-6 h-6 text-gray-400" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h1 className="text-2xl md:text-3xl font-black tracking-tight leading-tight" data-testid="minisite-title">
                        {data.salon_name}
                      </h1>
                      {address && (
                        <a href={mapLinkUrl} target="_blank" rel="noopener noreferrer"
                           className="block mt-1 text-sm text-gray-600 hover:underline" data-testid="minisite-hero-address">
                          {address}
                        </a>
                      )}
                    </div>
                  </div>

                  {(ms.tagline || "").trim() && (
                    <p className="mt-3 text-sm md:text-base text-gray-700" data-testid="minisite-tagline">{ms.tagline}</p>
                  )}

                  {(social.phone || "").trim() && (
                    <div className="mt-3">
                      {phoneShown ? (
                        <a href={`tel:${social.phone.replace(/\s/g, "")}`}
                           className="inline-flex items-center gap-1.5 text-sm md:text-base font-bold hover:underline"
                           style={{ color: accent }} data-testid="minisite-hero-phone">
                          📞 {social.phone}
                        </a>
                      ) : (
                        <span className="inline-flex items-center gap-2 text-sm text-gray-700">
                          📞 {social.phone.slice(0, 6)}…
                          <button type="button" onClick={() => setPhoneShown(true)}
                                  className="font-semibold underline underline-offset-2 hover:text-black"
                                  style={{ color: accent }} data-testid="minisite-show-phone-btn">
                            {t("miniSite.showPhone") || "Показать"}
                          </button>
                        </span>
                      )}
                    </div>
                  )}
                  {data.website_url && (
                    <a href={data.website_url} target="_blank" rel="noopener noreferrer"
                       className="inline-flex items-center gap-1.5 mt-2 text-sm font-medium text-gray-600 hover:underline">
                      <Globe className="w-4 h-4" />
                      {data.website_url.replace(/^https?:\/\//, "")}
                    </a>
                  )}

                  <div className="mt-auto pt-4 flex flex-wrap gap-2">
                    <button onClick={bookingWidgetShown ? () => document.getElementById("ms-booking")?.scrollIntoView({ behavior: "smooth", block: "start" }) : openChat}
                            className="flex-1 min-w-[160px] text-white px-6 py-3 rounded-xl font-bold transition-opacity hover:opacity-85"
                            style={{ backgroundColor: accent }} data-testid="minisite-cta-chat">
                      {t("miniSite.bookNow") || "Записаться"}
                    </button>
                    {(social.whatsapp || "").trim() && (
                      <a href={`https://wa.me/${social.whatsapp.replace(/[^0-9]/g, "")}`} target="_blank" rel="noopener noreferrer"
                         className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl border-2 font-bold bg-white hover:bg-gray-50 transition-colors"
                         style={{ borderColor: accent, color: accent }} data-testid="minisite-cta-whatsapp">
                        WhatsApp
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </section>

            {/* Description + Map — DIKIDI-style split card */}
            {(infoBlocks.length > 0 || address) && (
              <section className="ms-fade bg-white rounded-2xl shadow-sm border border-gray-200 p-4 md:p-5 mb-4" style={{ animationDelay: "0.1s" }} data-testid="minisite-about-card">
                <div className={`grid gap-5 ${address ? "md:grid-cols-2" : ""}`}>
                  <div>
                    <h2 className="text-lg md:text-xl font-bold mb-3">
                      {tUi("section_about", "miniSite.section.about") || "Описание"}
                    </h2>
                    {infoBlocks.length > 0 ? (
                      <div className="space-y-3 text-sm md:text-base leading-relaxed text-gray-800 whitespace-pre-wrap">
                        {infoBlocks.map((block, i) => (
                          <p key={i}>{block}</p>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">
                        {t("miniSite.empty") || "Salon description coming soon. Meanwhile — ask the AI in the chat 👉"}
                      </p>
                    )}
                  </div>
                  {address && (
                    <div>
                      <h2 className="text-lg md:text-xl font-bold mb-3">
                        {t("miniSite.onMap") || "На карте"}
                      </h2>
                      <div className="rounded-xl overflow-hidden border border-gray-200 relative">
                        <iframe
                          title={`Map — ${data.salon_name}`}
                          src={mapEmbedUrl}
                          loading="lazy"
                          referrerPolicy="no-referrer-when-downgrade"
                          className="w-full h-48 md:h-56 block border-0"
                          data-testid="minisite-map-iframe"
                        />
                      </div>
                      <a href={mapLinkUrl} target="_blank" rel="noopener noreferrer"
                         className="inline-block mt-2 text-sm font-semibold underline underline-offset-4"
                         style={{ color: accent }} data-testid="minisite-open-maps">
                        {tUi("map_open", "miniSite.map.openInMaps")} →
                      </a>
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* Online booking — real free slots (or lead form when calendar disabled) */}
            {bookingWidgetShown && (
              <MiniSiteBooking cfg={bookCfgTr} salonId={data.salon_id} accent={accent} visitorLang={visitorLang}
                tr={trIsApplied ? { services: trCatalog, categories: trCategoryByRaw, levels: trLevels } : null}
                privacy={bookCfg.privacy || null}
                salonName={data.salon_name} salonPhone={social.phone || ""} whatsapp={social.whatsapp || ""} />
            )}

            {/* Team / Specialists — DIKIDI-style card */}
            {team.length > 0 && (
              <section className="ms-fade bg-white rounded-2xl shadow-sm border border-gray-200 p-4 md:p-5 mb-4" style={{ animationDelay: "0.15s" }} data-testid="minisite-team">
                <h2 className="text-lg md:text-xl font-bold mb-4">
                  {t("miniSite.section.team") || "Специалисты"}
                  <span className="ml-2 text-sm font-normal text-gray-500">{team.length}</span>
                </h2>
                {teamGroups.map((g, gi) => (
                <div key={g.branch?.id || "all"} className={gi > 0 ? "mt-5" : ""} data-testid={g.branch ? `minisite-team-branch-${g.branch.id}` : undefined}>
                {g.branch && (
                  <div className="flex items-baseline gap-2 mb-3">
                    <h3 className="text-sm md:text-base font-bold text-gray-900">📍 {g.branch.name}</h3>
                    {g.branch.address && <span className="text-xs text-gray-500 truncate">{g.branch.address}</span>}
                  </div>
                )}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {(showAllTeam ? g.members : g.members.slice(0, 4)).map((m, i) => (
                    <div
                      key={m.id || `${m.name}-${i}`}
                      className="border border-gray-200 rounded-xl bg-white p-4 flex flex-col items-center text-center"
                      data-testid={`minisite-team-card-${gi}-${i}`}
                    >
                      {m.photo_url ? (
                        <img
                          src={m.photo_url}
                          alt={m.name}
                          loading="lazy"
                          className="w-16 h-16 md:w-20 md:h-20 rounded-full object-cover bg-gray-100"
                          onError={(e) => { e.target.style.display = "none"; }}
                        />
                      ) : (
                        <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-gray-100 flex items-center justify-center">
                          <Sparkles className="w-6 h-6 text-gray-400" />
                        </div>
                      )}
                      <div className="mt-2.5 font-bold text-sm text-gray-900 leading-tight">{m.name}</div>
                      {m.level_name && <LevelBadge name={m.level_name} stars={m.stars} className="mt-1.5" />}
                      {(m.role || "").trim() && (
                        <div className="mt-1 text-xs text-gray-500 leading-snug line-clamp-2">{m.role}</div>
                      )}
                      {(m.services || []).length > 0 && (
                        <div className="mt-1 text-[11px] text-gray-500 leading-snug line-clamp-2" data-testid={`minisite-team-services-${gi}-${i}`}>{m.services.join(" · ")}</div>
                      )}
                      <button
                        type="button"
                        onClick={() => { pickStaff(m); bookAction(); }}
                        className="mt-3 px-4 py-1.5 rounded-lg text-xs font-bold border transition-colors hover:text-white"
                        style={{ borderColor: accent, color: accent }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = accent; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = ""; }}
                        data-testid={`minisite-team-book-${gi}-${i}`}
                      >
                        {t("miniSite.bookNow") || "Записаться"}
                      </button>
                    </div>
                  ))}
                </div>
                </div>
                ))}
                {teamGroups.some((g) => g.members.length > 4) && (
                  <div className="pt-3 text-center">
                    <button type="button" onClick={() => setShowAllTeam(!showAllTeam)} data-testid="minisite-team-viewall"
                      className="px-6 py-2.5 rounded-xl text-sm font-bold bg-gray-100 hover:bg-gray-200 transition-colors">
                      {showAllTeam ? (t("miniSite.services.collapse") || "Свернуть") : `${t("miniSite.services.viewAll") || "Показать все"} (${team.length})`}
                    </button>
                  </div>
                )}
              </section>
            )}

            {/* Services catalog — only when there is no booking wizard (the wizard's step 1 already lists services & prices) */}
            {flatServices.length > 0 && !bookingWidgetShown && (
              <section className="ms-fade bg-white rounded-2xl shadow-sm border border-gray-200 p-4 md:p-5 mb-4" style={{ animationDelay: "0.2s" }} data-testid="minisite-services">
                <h2 className="text-lg md:text-xl font-bold mb-2">
                  {tUi("section_services", "miniSite.section.services")}
                  <span className="ml-2 text-sm font-normal text-gray-500">{flatServices.length}</span>
                </h2>
                {(() => {
                  const cats = [];
                  flatServices.forEach((it) => { const k = it.catName || ""; let g = cats.find((c) => c.key === k); if (!g) { g = { key: k, items: [] }; cats.push(g); } g.items.push(it); });
                  const hasCats = cats.some((c) => c.key);
                  const renderRows = (items) => (
                    <ul className="divide-y divide-gray-100">
                      {items.map((it, i) => (
                    <li key={it.id || `${it.name}-${i}`} className="py-3 flex items-center gap-3 flex-wrap" data-testid={`minisite-service-row-${i}`}>
                        <div className="flex-1 min-w-[170px]">
                          <div className="font-semibold text-sm md:text-base text-gray-900">{it.name}</div>
                          {(it.description || it.catName) && (
                            <div className="text-xs text-gray-500 mt-0.5">{it.description || it.catName}</div>
                          )}
                        </div>
                        {it.price && (
                          <div className="text-sm md:text-base font-bold whitespace-nowrap w-20 text-right" style={{ color: accent }}>
                            {it.priceFrom && <span className="text-[11px] font-medium text-gray-400 mr-1">{lang === "ru" ? "от" : lang === "fr" ? "dès" : lang === "tr" ? "itibaren" : "from"}</span>}{formatPrice(it.price)}
                          </div>
                        )}
                        {it.duration && (
                          <div className="text-xs md:text-sm text-gray-500 whitespace-nowrap w-16 text-right">{it.duration}</div>
                        )}
                        <button
                          type="button"
                          onClick={bookAction}
                          className="px-3.5 py-1.5 rounded-lg text-xs font-bold text-white transition-opacity hover:opacity-85"
                          style={{ backgroundColor: accent }}
                          data-testid={`minisite-service-book-${it.id || it.name}`}
                        >
                          {t("miniSite.bookNow") || "Записаться"}
                        </button>
                      </li>
                        ))}
                    </ul>
                  );
                  if (!hasCats) return renderRows(visibleServices);
                  return cats.map((c, ci) => {
                    const open = openCats[c.key] ?? ci === 0;
                    return (
                      <div key={c.key || "_none"} className="border border-gray-200 rounded-xl mb-2 overflow-hidden" data-testid={`minisite-service-cat-${ci}`}>
                        <button type="button" onClick={() => setOpenCats({ ...openCats, [c.key]: !open })} data-testid={`minisite-service-cat-toggle-${ci}`}
                          className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50">
                          <span className="font-bold text-sm md:text-base text-gray-900">{c.key || ({ ru: "Другое", fr: "Autres", tr: "Diğer" }[lang] || "Other")} <span className="ml-1 text-xs font-normal text-gray-500">{c.items.length}</span></span>
                          <span className="text-gray-400">{open ? "▴" : "▾"}</span>
                        </button>
                        {open && <div className="px-4 pb-1">{renderRows(c.items)}</div>}
                      </div>
                    );
                  });
                })()}
                {flatServices.length > 8 && !flatServices.some((it) => it.catName) && (
                  <div className="pt-3 text-center">
                    <button
                      type="button"
                      onClick={() => setShowAllServices(!showAllServices)}
                      className="px-6 py-2.5 rounded-xl text-sm font-bold bg-gray-100 hover:bg-gray-200 transition-colors"
                      data-testid="minisite-services-viewall"
                    >
                      {showAllServices
                        ? (t("miniSite.services.collapse") || "Свернуть")
                        : `${t("miniSite.services.viewAll") || "Показать все"} (${flatServices.length})`}
                    </button>
                  </div>
                )}
              </section>
            )}

            {/* Branches: address + phone + weekly hours */}
            {branches.length > 0 && (
              <section className="ms-fade bg-white rounded-2xl shadow-sm border border-gray-200 p-4 md:p-5 mb-4" style={{ animationDelay: "0.25s" }} data-testid="minisite-branches">
                <h2 className="text-lg md:text-xl font-bold mb-3">🕒 {tUi("section_hours", "miniSite.section.hours")}</h2>
                <div className={`grid gap-4 ${branches.length > 1 ? "md:grid-cols-2" : ""}`}>
                  {branches.map((b) => (
                    <div key={b.id} className="rounded-xl border border-gray-100 bg-gray-50/60 p-3.5" data-testid={`minisite-branch-${b.id}`}>
                      {branches.length > 1 && <div className="font-bold text-[15px] mb-1">{b.name}</div>}
                      {b.address && branches.length > 1 && (
                        <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(b.address)}`} target="_blank" rel="noreferrer"
                          className="block text-sm text-gray-800 underline decoration-gray-300 underline-offset-2 hover:decoration-gray-800 mb-1" data-testid="minisite-branch-address">📍 {b.address}</a>
                      )}
                      {b.phone && <a href={`tel:${b.phone}`} className="block text-sm text-gray-800 mb-2" data-testid="minisite-branch-phone">📞 {b.phone}</a>}
                      <div className="text-sm text-gray-900 space-y-0.5">
                        {scheduleLines(b.schedule).map((l, i) => (
                          <div key={i} className="flex justify-between gap-3"><span className="text-gray-500">{l.label}</span><span className={`font-medium tabular-nums ${l.value === closedLabel ? "text-gray-400" : ""}`}>{l.value}</span></div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
            {/* Working hours (legacy free text, only when no CRM branches) */}
            {hours && branches.length === 0 && (
              <section className="ms-fade bg-white rounded-2xl shadow-sm border border-gray-200 p-4 md:p-5 mb-4" style={{ animationDelay: "0.25s" }} data-testid="minisite-hours">
                <h2 className="text-lg md:text-xl font-bold mb-3">
                  🕒 {tUi("section_hours", "miniSite.section.hours")}
                </h2>
                <div className="whitespace-pre-line text-gray-900 text-sm md:text-base">
                  {hours}
                </div>
              </section>
            )}

            {/* Photo gallery (click → in-page lightbox) */}
            {photos.length > 0 && (
              <section className="ms-fade bg-white rounded-2xl shadow-sm border border-gray-200 p-4 md:p-5 mb-4" style={{ animationDelay: "0.3s" }} data-testid="minisite-gallery">
                <h2 className="text-lg md:text-xl font-bold mb-4">
                  🖼 {t("miniSite.section.gallery")}
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {photos.map((p, i) => (
                    <button
                      key={p.id || `${p.url}-${i}`}
                      type="button"
                      onClick={() => setLightboxIdx(i)}
                      className="relative block aspect-square rounded-xl overflow-hidden bg-gray-100 group cursor-zoom-in"
                      data-testid={`minisite-gallery-thumb-${i}`}
                      aria-label={p.alt || `Photo ${i + 1}`}
                    >
                      <img
                        src={p.url}
                        alt={p.alt || data.salon_name}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        onError={(e) => { e.target.style.display = "none"; }}
                      />
                    </button>
                  ))}
                </div>
              </section>
            )}

            {/* Contacts (address lives in the hero + map card) */}
            {socialIcons.length > 0 && (
              <section className="ms-fade bg-white rounded-2xl shadow-sm border border-gray-200 p-4 md:p-5 mb-4" style={{ animationDelay: "0.35s" }} data-testid="minisite-contacts">
                <h2 className="text-lg md:text-xl font-bold mb-4">
                  📍 {tUi("section_contacts", "miniSite.section.contacts")}
                </h2>

                <div className="flex flex-wrap gap-2">
                  {socialIcons.map((s) => (
                    <a
                      key={s.key}
                      href={s.href}
                      target={s.key === "phone" || s.key === "email" ? undefined : "_blank"}
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 bg-white text-sm font-medium hover:bg-gray-50 transition-colors"
                      data-testid={`minisite-social-link-${s.key}`}
                    >
                      <span className="text-base">{s.label}</span>
                      <span className="truncate max-w-[220px]">{s.text}</span>
                    </a>
                  ))}
                </div>
              </section>
            )}
          </div>
        </div>

        {/* Floating chat bubble (auto-opens via ?open=1 in URL) */}
        <ChatWidget
          salonId={data.salon_id}
          config={{
            salon_name: data.salon_name,
            privacy: bookCfg?.privacy || null,
            widget_settings: {
              logo_url: data.logo_url,
              primary_color: data.primary_color,
              welcome_message: data.welcome_message,
              quick_replies: data.quick_replies,
            },
            business_type: data.business_type,
          }}
        />

        {/* Minimal "Powered by Chatity" footer */}
        <footer className="border-t border-gray-200 py-5 text-center text-xs text-gray-400">
          Powered by{" "}
          <a
            href="https://chatity.io"
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-gray-600 hover:text-black transition-colors underline-offset-2 hover:underline"
            data-testid="minisite-powered-by"
          >
            chatity.io
          </a>
        </footer>
      </div>

      {/* Lightbox modal — opens on gallery thumb click */}
      {lightboxIdx >= 0 && photos[lightboxIdx] && (
        <Lightbox
          photos={photos}
          index={lightboxIdx}
          onIndex={setLightboxIdx}
          onClose={() => setLightboxIdx(-1)}
          t={t}
        />
      )}
    </>
  );
}

/** In-page lightbox modal with keyboard (← → Esc) and click-outside-to-close. */
function Lightbox({ photos, index, onIndex, onClose, t }) {
  const cur = photos[index];

  const go = useCallback((dir) => {
    const n = photos.length;
    if (n <= 1) return;
    onIndex(((index + dir) % n + n) % n);
  }, [index, photos.length, onIndex]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") go(-1);
      else if (e.key === "ArrowRight") go(1);
    };
    window.addEventListener("keydown", onKey);
    // lock body scroll
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [go, onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center p-4 backdrop-blur-sm"
      data-testid="minisite-lightbox"
    >
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        aria-label={t("miniSite.gallery.close")}
        className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
        data-testid="minisite-lightbox-close"
      >
        <X className="w-6 h-6" />
      </button>

      {photos.length > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); go(-1); }}
            aria-label={t("miniSite.gallery.prev")}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
            data-testid="minisite-lightbox-prev"
          >
            <ChevronLeft className="w-7 h-7" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); go(1); }}
            aria-label={t("miniSite.gallery.next")}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
            data-testid="minisite-lightbox-next"
          >
            <ChevronRight className="w-7 h-7" />
          </button>
        </>
      )}

      <img
        src={cur.url}
        alt={cur.alt || ""}
        onClick={(e) => e.stopPropagation()}
        className="max-w-[92vw] max-h-[88vh] object-contain rounded-lg shadow-2xl select-none"
        data-testid="minisite-lightbox-image"
      />

      <div
        onClick={(e) => e.stopPropagation()}
        className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/80 text-sm bg-black/40 px-3 py-1 rounded-full"
      >
        {index + 1} / {photos.length}
      </div>
    </div>
  );
}
